import { motion } from 'motion/react';

import { cn } from '@april9au/react-common/utils';

import type { LanguageOption } from '@/i18n/languages';
import { pressSpring } from '@/components/motion/tokens';
import { pressProps, stagedChild } from '@/components/motion/variants';

/**
 * The welcome screen's language picker: the flow's first question, asked on the
 * navy glass card between the facts panel and the Start button.
 *
 * It differs from `LanguageTiles` — the full-width rows the header sheet uses —
 * in the two ways this placement demands.
 *
 * Nine languages have to fit above the Start button on a 390 × 844 viewport
 * without eating the photograph, so a language is a compact tile in a 3 × 3
 * grid rather than a row. A grid was chosen over a snap-scrolling strip because
 * the point of moving this screen-forward is *discovery*: someone who reads no
 * English has to see their language, and a strip shows three of nine and hides
 * the rest behind a gesture they have no reason to try. The grid also inherits
 * mirroring from `dir` alone, so Arabic needs no second implementation.
 *
 * And it has no unselected state. The page is *already* rendering in a
 * language, so that language is ticked from the first paint and a tap moves the
 * tick rather than creating it.
 *
 * Nothing here schedules the entrance: the wrapper inherits the card's motion
 * label, and no tile animates on its own.
 */
export function WelcomeLanguagePicker({
  languages,
  selected,
  onSelect,
  label,
}: {
  languages: Array<LanguageOption>;
  /** Always a real code: the language the page is rendering in. */
  selected: string;
  onSelect: (code: string) => void;
  label: string;
}) {
  return (
    <motion.div {...stagedChild} className="w-full space-y-2 text-start">
      {/*
        A field label, not a heading. `language_title` ("Please select your
        language") is a question screen's h1 and reads as an instruction to obey
        before anything else on the card; this names the control and lets Start
        stay the screen's call to action.
      */}
      <span className="block text-sm leading-none font-semibold text-white/80">{label}</span>

      <ul className="grid grid-cols-3 gap-1.5">
        {languages.map((language) => (
          <li key={language.code}>
            <LanguageTile language={language} selected={language.code === selected} onSelect={onSelect} />
          </li>
        ))}
      </ul>
    </motion.div>
  );
}

/**
 * One language. The colours are the card's own two: the facts panel's hairline
 * and 8 % white ground for a language you could pick, and the flow's yellow for
 * the one you are reading — and the current tile is the only one with a filled
 * ground and a heavier border, so the state never rides on colour alone.
 */
function LanguageTile({
  language,
  selected,
  onSelect,
}: {
  language: LanguageOption;
  selected: boolean;
  onSelect: (code: string) => void;
}) {
  return (
    <motion.button
      type="button"
      onClick={() => onSelect(language.code)}
      aria-pressed={selected}
      {...pressProps}
      transition={pressSpring}
      className={cn(
        'bp-tappable flex w-full items-center rounded-xl border text-white',
        'transition-[background-color,border-color] duration-[var(--bp-duration-fast)] motion-reduce:transition-none',
        'focus-visible:ring-2 focus-visible:ring-white focus-visible:outline-none',
        selected
          ? 'border-bp-yellow bg-bp-yellow/20 font-bold'
          : 'border-bp-yellow/25 hover:border-bp-yellow/50 bg-white/8 font-semibold hover:bg-white/12',
        // 44 px is the floor, and it is what a short viewport gets; the extra
        // 4 px elsewhere is comfort, not reach.
        'min-h-12 flex-col justify-center gap-1 px-1 py-1.5 [@media(max-height:800px)]:min-h-11',
        // A 100 px tile on a phone can only stack the code over the name; a
        // 140 px one on a desktop looks half-empty doing it, and the row it
        // makes instead is also a row shorter overall.
        'sm:min-h-11 sm:flex-row sm:gap-2 sm:px-2 sm:py-2',
      )}
    >
      {language.flagUrl ? (
        /* Fixed box + `object-cover`: a flag from Prismic has no knowable
           ratio, and 3:2 is the aspect the bundled set is drawn at.
           Decorative — the native name beside it is the control's name. */
        <img
          src={language.flagUrl}
          alt=""
          width={21}
          height={14}
          className="h-3.5 w-[1.3125rem] shrink-0 rounded-[0.1875rem] object-cover ring-1 ring-white/25"
        />
      ) : (
        /* The ISO code in the flag's place, e.g. ZH beside 普通话 and YUE
           beside 廣東話, so the two Chinese options read apart at a glance. */
        <span
          aria-hidden
          translate="no"
          className="text-bp-yellow shrink-0 text-[0.625rem] leading-none font-bold tracking-wider sm:text-xs"
        >
          {language.codeLabel}
        </span>
      )}

      {/* `leading-[1.6]` rather than the tighter step the rest of the card
          uses: `truncate` clips the line box, and Punjabi and Hindi hang marks
          below the baseline that a 1.2 line box cuts through. */}
      <span lang={language.code} className="block max-w-full truncate text-[0.6875rem] leading-[1.6] sm:text-sm">
        {language.nativeName}
      </span>
    </motion.button>
  );
}
